"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Pencil, Trash } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { authFetch } from "@/lib/authClient";

type AdminActionsProps = {
  id: string | number;
  onDeleted?: () => void;
  entity?: "news" | "section-gallery" | string;
  editHref?: string;
};

export default function AdminActions({
  id,
  onDeleted,
  entity = "news",
  editHref,
}: AdminActionsProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const router = useRouter();

  const handleEdit = () => {
    if (editHref) {
      router.push(editHref);
      return;
    }
    if (entity === "section-gallery") {
      router.push(`/area-riservata/gallery/edit?id=${id}`);
      return;
    }
    router.push(`/admin/${entity}/edit-${entity}/?id=${id}`);
  };

  async function handleDelete() {
    try {
      const res = await authFetch(
        entity === "section-gallery"
          ? `/api/section-gallery/activities/${id}`
          : `/api/${entity}/${id}`,
        { method: "DELETE" },
      );

      if (!res.ok) {
        // prova a leggere l'errore dal server
        let msg = "";
        try {
          const j = await res.clone().json();
          msg = j?.error || JSON.stringify(j);
        } catch {
          msg = await res.text();
        }
        toast.error(`Eliminazione fallita (${res.status}) ${msg}`.trim());
        return;
      }

      toast.success("Elemento eliminato con successo.");
      setConfirmDelete(false);
      onDeleted?.();
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'eliminazione.");
    }
  }

  return (
    <div className="flex justify-center gap-4">
      <Button
        className="cursor-pointer bg-(--btn-bg) text-(--btn-foreground) font-semibold text-base sm:text-lg md:text-xl px-6 py-4 sm:px-8 sm:py-4 shadow-md transition-all duration-300 hover:bg-(--btn-hover) hover:shadow-lg"
        onClick={handleEdit}
      >
        <Pencil className="w-4 h-4 mr-2" />
        Modifica
      </Button>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogTrigger asChild>
          <Button
            className="cursor-pointer bg-red-600 text-white font-semibold text-base sm:text-lg md:text-lg px-6 py-4 sm:px-8 sm:py-4 shadow-md transition-all duration-300 hover:shadow-lg"
            variant="destructive"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash className="w-4 h-4 mr-2" />
            Elimina
          </Button>
        </DialogTrigger>

        <DialogContent className="bg-(--background)">
          <DialogHeader>
            <DialogTitle>Sei sicuro?</DialogTitle>
          </DialogHeader>
          <p>
            Questa operazione è irreversibile. Vuoi davvero eliminare questo
            elemento?
          </p>
          <DialogFooter className="mt-4">
            <Button
              className="cursor-pointer"
              variant="outline"
              onClick={() => setConfirmDelete(false)}
            >
              Annulla
            </Button>
            <Button
              className="cursor-pointer"
              variant="destructive"
              onClick={handleDelete}
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}